import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";

const EscalateDialog = ({ open, agents = [], currentAssigneeId, escalations = [], onClose, onConfirm }) => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [reason, setReason] = useState("");
  const [agentSearch, setAgentSearch] = useState("");

  // Exclude current assignee and already escalated agents
  const escalatedIds = escalations.map((e) => e.escalated_to_agent_id);
  const excludeIds = [currentAssigneeId, ...escalatedIds].filter(Boolean);

  const filteredAgents = agents.filter(
    (a) =>
      !excludeIds.includes(a.id) &&
      (a.name?.toLowerCase().includes(agentSearch.toLowerCase()) ||
        a.email?.toLowerCase().includes(agentSearch.toLowerCase()))
  );

  const handleConfirm = () => {
    if (selectedAgent) {
      onConfirm(selectedAgent, reason);
      setSelectedAgent(null);
      setReason("");
      setAgentSearch("");
    }
  };

  const handleClose = () => {
    setSelectedAgent(null);
    setReason("");
    setAgentSearch("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          Escalate Ticket
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}>
          Add another agent to assist with this ticket.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Agent Selection */}
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>
          Select Agent
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search agents..."
          value={agentSearch}
          onChange={(e) => setAgentSearch(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ maxHeight: 200, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: "8px", mb: 3 }}>
          <List dense disablePadding>
            {filteredAgents.map((agent) => {
              const isSelected = selectedAgent?.id === agent.id;
              return (
                <ListItem
                  key={agent.id}
                  button
                  onClick={() => setSelectedAgent(agent)}
                  sx={{
                    backgroundColor: isSelected ? "#EFF6FF" : "transparent",
                    borderLeft: isSelected ? "3px solid #0369A1" : "3px solid transparent",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 13, backgroundColor: "#0369A1" }}>
                      {(agent.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={agent.name}
                    secondary={agent.email}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItem>
              );
            })}
            {filteredAgents.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#64748B" }}>No available agents found</Typography>
              </Box>
            )}
          </List>
        </Box>

        {/* Reason */}
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
          Reason (optional)
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={3}
          placeholder="Why is this ticket being escalated?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", color: "#64748B" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedAgent}
          sx={{
            textTransform: "none",
            backgroundColor: "#D97706",
            "&:hover": { backgroundColor: "#b45309" },
          }}
        >
          Escalate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EscalateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  agents: PropTypes.array,
  currentAssigneeId: PropTypes.string,
  escalations: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default EscalateDialog;
